/**
 * KochID OIDC client — discovery, authorization URL construction, and token
 * exchange for the Authorization Code + PKCE flow (ADR 0012).
 *
 * KochID is registered as a *public* client for this application: the token
 * endpoint accepts the code exchange with no client authentication, verified
 * against the live endpoint. No client secret is used, referenced, or required
 * anywhere in this module. If a future change makes the client confidential,
 * the exchange must move server-side — a browser must never hold a secret.
 */

import { createCodeVerifier, createState, deriveCodeChallenge } from "./pkce";

/** The subset of the OIDC discovery document this client depends on. */
interface DiscoveryDocument {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  userinfo_endpoint?: string;
  end_session_endpoint?: string;
}

/** Token response fields used by the application. */
export interface TokenSet {
  accessToken: string;
  idToken: string;
  refreshToken: string | null;
  /** Epoch milliseconds at which the access token expires. */
  expiresAt: number;
}

/** Identity claims read from the ID token for display purposes only. */
export interface UserClaims {
  sub: string;
  name: string;
  email: string;
}

export interface OidcConfig {
  issuer: string;
  clientId: string;
  redirectUri: string;
  scopes: string;
}

/**
 * Read OIDC configuration from the environment. Returns null when the app has
 * not been configured, so the UI can degrade to an explicit "not configured"
 * state instead of throwing on load.
 */
export function readOidcConfig(): OidcConfig | null {
  const issuer = import.meta.env.VITE_KOCHID_ISSUER;
  const clientId = import.meta.env.VITE_KOCHID_CLIENT_ID;
  const scopes = import.meta.env.VITE_KOCHID_SCOPES;

  if (!issuer || !clientId || !scopes) {
    return null;
  }

  // Derive the redirect URI from the origin the user actually loaded, rather
  // than a fixed configured value. This is a correctness requirement, not a
  // convenience: the PKCE verifier and state are written to THIS origin's
  // sessionStorage, so the callback must return to the same origin or it
  // cannot read them back and is refused as a state mismatch.
  //
  // A hardcoded redirect_uri breaks the moment the app is reachable at more
  // than one hostname — which it is: each environment serves at both its
  // *.amplifyapp.com default URL and its inventory.kochcc.com custom domain,
  // plus localhost in development. Whichever origin the user starts on, the
  // IdP returns them there. Every such origin's /callback must be registered
  // with KochID.
  const redirectUri = `${window.location.origin}/callback`;

  return { issuer, clientId, redirectUri, scopes };
}

let discoveryCache: DiscoveryDocument | null = null;

/**
 * Fetch and cache the OIDC discovery document. Endpoints are resolved from
 * discovery rather than hardcoded so the client keeps working if KochID moves
 * an endpoint (ADR 0012).
 */
export async function loadDiscovery(issuer: string): Promise<DiscoveryDocument> {
  if (discoveryCache && discoveryCache.issuer === issuer) {
    return discoveryCache;
  }

  const url = `${issuer.replace(/\/$/, "")}/.well-known/openid-configuration`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `OIDC discovery failed: ${response.status} ${response.statusText} from ${url}`,
    );
  }

  const document = (await response.json()) as DiscoveryDocument;
  if (!document.authorization_endpoint || !document.token_endpoint) {
    throw new Error("OIDC discovery document is missing required endpoints.");
  }

  discoveryCache = document;
  return document;
}

/**
 * Storage keys for the values that must survive the authorization redirect.
 *
 * DEVIATION, deliberate: conventions/frontend-stack.md says to hold the code
 * verifier in memory only. That is not achievable for a redirect-based flow —
 * a full-page navigation to the identity provider destroys all in-memory
 * state, so the verifier could never be presented at the token endpoint and
 * no redirect flow could ever complete.
 *
 * sessionStorage is the narrowest store that survives the redirect: it is
 * scoped to the tab, cleared when the tab closes, and never shared with other
 * tabs or windows. Both entries are deleted the moment the code is exchanged,
 * so the window in which they exist is one round trip. The verifier is
 * single-use and worthless afterward.
 *
 * Tokens are NOT stored here — the access, ID, and refresh tokens are held in
 * memory only, exactly as the convention requires.
 */
const VERIFIER_KEY = "kcc-pkce-verifier";
const STATE_KEY = "kcc-pkce-state";
const RETURN_TO_KEY = "kcc-post-login-return-to";

/**
 * Begin the Authorization Code + PKCE flow by redirecting to KochID.
 * Records where the user was so they can be returned there after login.
 */
export async function beginLogin(config: OidcConfig, returnTo: string): Promise<void> {
  const discovery = await loadDiscovery(config.issuer);

  const verifier = createCodeVerifier();
  const state = createState();
  const challenge = await deriveCodeChallenge(verifier);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_TO_KEY, returnTo);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scopes,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  window.location.assign(`${discovery.authorization_endpoint}?${params.toString()}`);
}

/** True when the current URL looks like an OIDC redirect callback. */
export function isCallbackUrl(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has("code") || params.has("error");
}

/** Where the user should be sent after a completed login. */
export function consumeReturnTo(): string {
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY) ?? "/";
  sessionStorage.removeItem(RETURN_TO_KEY);
  return returnTo;
}

/**
 * Complete the flow: validate `state`, exchange the code for tokens, and clear
 * the transient PKCE values. Throws with a descriptive message on any failure
 * — the caller decides how to surface it.
 */
export async function completeLogin(config: OidcConfig): Promise<TokenSet> {
  const params = new URLSearchParams(window.location.search);

  const error = params.get("error");
  if (error) {
    const description = params.get("error_description") ?? "no description provided";
    clearTransientState();
    throw new Error(`KochID returned an error: ${error} — ${description}`);
  }

  const code = params.get("code");
  const returnedState = params.get("state");
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);

  if (!code) {
    clearTransientState();
    throw new Error("Callback is missing the authorization code.");
  }
  if (!expectedState || returnedState !== expectedState) {
    // A mismatch means the callback did not originate from a login this tab
    // started. Refuse it rather than exchanging the code.
    clearTransientState();
    throw new Error("Authorization state mismatch — refusing the callback.");
  }
  if (!verifier) {
    clearTransientState();
    throw new Error("PKCE code verifier is missing — cannot complete login.");
  }

  const discovery = await loadDiscovery(config.issuer);

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  });

  // Clear before awaiting so the single-use verifier cannot be replayed even
  // if the exchange fails.
  clearTransientState();

  const response = await fetch(discovery.token_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!response.ok) {
    const detail = await safeErrorDetail(response);
    throw new Error(`Token exchange failed: ${response.status} — ${detail}`);
  }

  return toTokenSet(await response.json());
}

/** Exchange a refresh token for a new token set. */
export async function refreshTokens(
  config: OidcConfig,
  refreshToken: string,
): Promise<TokenSet> {
  const discovery = await loadDiscovery(config.issuer);

  const response = await fetch(discovery.token_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: refreshToken,
      client_id: config.clientId,
    }),
  });

  if (!response.ok) {
    const detail = await safeErrorDetail(response);
    throw new Error(`Token refresh failed: ${response.status} — ${detail}`);
  }

  return toTokenSet(await response.json());
}

/**
 * Decode the ID token payload for display. This is NOT verification — the ID
 * token is a display artifact only and is never sent to a backend or used for
 * an authorization decision (ADR 0012). Backends validate the access token.
 */
export function readClaims(idToken: string): UserClaims {
  const payload = idToken.split(".")[1];
  if (!payload) {
    throw new Error("ID token is malformed — no payload segment.");
  }

  const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
  const decoded = JSON.parse(
    decodeURIComponent(
      atob(normalized)
        .split("")
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join(""),
    ),
  ) as Record<string, unknown>;

  const sub = typeof decoded.sub === "string" ? decoded.sub : "";
  // Deliberately no placeholder here: an absent name must stay empty so
  // mergeClaims can fill it from UserInfo. Substituting "Unknown user" at this
  // point would win the merge and permanently mask the real name.
  const name =
    typeof decoded.name === "string"
      ? decoded.name
      : typeof decoded.preferred_username === "string"
        ? decoded.preferred_username
        : "";
  const email =
    typeof decoded.email === "string" && decoded.email
      ? decoded.email
      : emailFromSubject(sub);

  return { sub, name, email };
}

/**
 * KochID sets `sub` to the user's email address rather than an opaque
 * identifier — the UUID lives in the `p1.userId` claim. So when no `email`
 * claim is released, an email-shaped `sub` is a genuine identity worth
 * displaying, not a meaningless token. Anything not email-shaped is ignored.
 */
function emailFromSubject(sub: string): string {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(sub) ? sub : "";
}

/**
 * Fetch identity claims from the UserInfo endpoint.
 *
 * WHY THIS IS NEEDED. KochID issues a minimal ID token — in practice `sub` is
 * present but `name` and `email` frequently are not, even with the `profile`
 * and `email` scopes granted. The ID token is a proof of authentication, not a
 * guaranteed profile carrier, and an authorization server is free to release
 * profile claims only from UserInfo. `claims_supported` advertises `name`,
 * `preferred_username`, and `email`; `userinfo_endpoint` is where they are
 * reliably served.
 *
 * Called with the ACCESS token as a Bearer credential, per OIDC Core. This
 * does not conflict with ADR 0012's rule that the ID token is never sent to a
 * backend — the request goes to the identity provider, not to this
 * application's API, and carries the access token rather than the ID token.
 *
 * Returns null rather than throwing: a failure here should degrade the
 * displayed name, never break an otherwise valid session.
 */
export async function fetchUserInfo(
  config: OidcConfig,
  accessToken: string,
): Promise<Partial<UserClaims> | null> {
  try {
    const discovery = await loadDiscovery(config.issuer);
    if (!discovery.userinfo_endpoint) {
      return null;
    }

    const response = await fetch(discovery.userinfo_endpoint, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as Record<string, unknown>;
    const name =
      typeof data.name === "string"
        ? data.name
        : typeof data.preferred_username === "string"
          ? data.preferred_username
          : undefined;

    const sub = typeof data.sub === "string" ? data.sub : undefined;
    const email =
      typeof data.email === "string" && data.email
        ? data.email
        : sub
          ? emailFromSubject(sub) || undefined
          : undefined;

    return { sub, name, email };
  } catch {
    return null;
  }
}

/**
 * Merge UserInfo over ID token claims. Only non-empty values override, so a
 * claim present in the ID token is never replaced by a blank from UserInfo,
 * and the display works whichever source carries the profile.
 */
export function mergeClaims(
  base: UserClaims,
  extra: Partial<UserClaims> | null,
): UserClaims {
  if (!extra) {
    return base;
  }
  return {
    sub: extra.sub || base.sub,
    name: extra.name || base.name,
    email: extra.email || base.email,
  };
}

/** Build the end-session URL when KochID advertises one. */
export async function buildLogoutUrl(config: OidcConfig, idToken: string): Promise<string | null> {
  const discovery = await loadDiscovery(config.issuer);
  if (!discovery.end_session_endpoint) {
    return null;
  }
  const params = new URLSearchParams({
    id_token_hint: idToken,
    post_logout_redirect_uri: new URL(config.redirectUri).origin,
  });
  return `${discovery.end_session_endpoint}?${params.toString()}`;
}

function clearTransientState(): void {
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
}

function toTokenSet(raw: unknown): TokenSet {
  const data = raw as Record<string, unknown>;
  const accessToken = typeof data.access_token === "string" ? data.access_token : "";
  const idToken = typeof data.id_token === "string" ? data.id_token : "";
  const refreshToken = typeof data.refresh_token === "string" ? data.refresh_token : null;
  const expiresIn = typeof data.expires_in === "number" ? data.expires_in : 3600;

  if (!accessToken || !idToken) {
    throw new Error("Token response is missing access_token or id_token.");
  }

  return { accessToken, idToken, refreshToken, expiresAt: Date.now() + expiresIn * 1000 };
}

async function safeErrorDetail(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as Record<string, unknown>;
    const error = typeof body.error === "string" ? body.error : "unknown_error";
    const description =
      typeof body.error_description === "string" ? body.error_description : "";
    return description ? `${error}: ${description}` : error;
  } catch {
    return response.statusText || "no error detail available";
  }
}
